import React, { useState, useEffect, useCallback } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import NotFound from '../../components/UI/NotFound';
import { HiOutlinePlus, HiOutlineRefresh, HiOutlineSearch } from 'react-icons/hi';
import { FaEdit, FaTrashAlt, FaFolder, FaCopy, FaToggleOn, FaSortAmountDown, FaImages, FaTimesCircle } from 'react-icons/fa';

const PAGE_LIMIT = 10;

// ---- Centered Edit Modal ----
const EditModal = ({ category, onClose, onSaved }) => {
    const [name, setName] = useState(category.w_name || '');
    const [orderVal, setOrderVal] = useState(category.w_order ?? 0);
    const [status, setStatus] = useState(category.w_status);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!name?.trim()) {
            setError('Category name is required.');
            return;
        }
        setSaving(true);
        try {
            const resp = await Calling.updateWallpaperCategory(category._id, {
                w_name: name.trim(),
                w_order: Number(orderVal) || 0,
                w_status: status,
            });
            if (resp?.status) {
                toast.success(resp?.message);
                onSaved();
                onClose();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Something went wrong.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-main-blue/10 rounded-lg">
                            <FaEdit className="text-main-blue" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Edit Category</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
                        <FaTimesCircle size={22} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <FaFolder className="text-main-blue text-sm" /> Category Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                            placeholder="e.g., Nature, Abstract, Anime"
                        />
                        {error && <p className="text-xs mt-2 text-red-500">{error}</p>}
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <FaSortAmountDown className="text-main-blue text-sm" /> Order
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={orderVal}
                            onChange={(e) => setOrderVal(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                            placeholder="0"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <FaToggleOn className="text-main-blue text-sm" /> Status
                        </label>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={status}
                                onChange={() => setStatus(!status)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                            <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                        </label>
                    </div>

                    <div className="text-xs text-gray-400 font-mono pt-1">ID: {category._id}</div>
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        <FaEdit className="text-sm" />
                        {saving ? 'Updating...' : 'Update Category'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const WallpaperCategory = () => {
    const [loader, setLoader] = useState(false);
    const [categories, setCategories] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filters / paging
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // '' | 'true' | 'false'
    const [sortBy, setSortBy] = useState('w_order');
    const [order, setOrder] = useState('asc');

    // Add form
    const [name, setName] = useState('');
    const [orderVal, setOrderVal] = useState(0);
    const [status, setStatus] = useState(true);
    const [error, setError] = useState(null);

    // Bulk select
    const [selected, setSelected] = useState([]);

    // Edit modal
    const [editCategory, setEditCategory] = useState(null);

    const fetchData = useCallback(async () => {
        setLoader(true);
        try {
            const resp = await Calling.getWallpaperCategory({
                page, limit: PAGE_LIMIT, search, status: statusFilter, sortBy, order,
            });
            if (resp?.status) {
                setCategories(resp?.data || []);
                setTotalRecords(resp?.totalRecords || 0);
                setTotalPages(resp?.totalPages || 1);
            } else {
                setCategories([]);
                setTotalRecords(0);
                setTotalPages(1);
                if (resp?.message) toast.error(resp.message);
            }
        } catch (err) {
            toast.error('Failed to fetch categories');
        } finally {
            setLoader(false);
        }
    }, [page, search, statusFilter, sortBy, order]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const resetForm = () => {
        setName('');
        setOrderVal(0);
        setStatus(true);
        setError(null);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!name?.trim()) {
            setError('Category name is required.');
            return;
        }
        setLoader(true);
        try {
            const resp = await Calling.addWallpaperCategory({
                w_name: name.trim(),
                w_order: Number(orderVal) || 0,
                w_status: status,
            });
            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
                fetchData();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Something went wrong.');
        } finally {
            setLoader(false);
        }
    };

    const handleToggleStatus = async (c) => {
        setLoader(true);
        try {
            const resp = await Calling.updateWallpaperCategory(c._id, { w_status: !c.w_status });
            if (resp?.status) {
                toast.success(resp?.message);
                fetchData();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Failed to update status.');
        } finally {
            setLoader(false);
        }
    };

    const handleDelete = async () => {
        if (selected.length === 0) return toast.error('Please select at least one category to delete.');
        const check = generateCaptcha('You are about to delete selected categories. Solve this to confirm.');
        if (!check) return toast.error('Incorrect answer.');
        setLoader(true);
        try {
            const resp = await Calling.deleteWallpaperCategory({ id: selected });
            if (resp?.status) {
                toast.success(resp?.message);
                setSelected([]);
                fetchData();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Failed to delete categories.');
        } finally {
            setLoader(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    const toggleSelect = (id) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const selectAll = (e) => setSelected(e.target.checked ? categories.map((c) => c._id) : []);

    const copyId = (id) => {
        navigator.clipboard.writeText(id);
        toast.success('ID copied to clipboard!');
    };

    const copySlug = (slug) => {
        navigator.clipboard.writeText(slug);
        toast.success('Slug copied to clipboard!');
    };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Wallpaper Categories</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage wallpaper categories — order, status &amp; more</p>
                            </div>
                            <button
                                onClick={fetchData}
                                className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white transition-colors duration-200 shadow-sm"
                            >
                                <HiOutlineRefresh className="text-lg" />
                                <span>Refresh</span>
                            </button>
                        </div>

                        {/* Add Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                        <HiOutlinePlus className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Add New Category</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">Slug is auto-generated from the name</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                <form onSubmit={handleAdd} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                            <FaFolder className="text-main-blue text-sm" /> Category Name
                                        </label>
                                        <div className="md:col-span-2">
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                placeholder="e.g., Nature, Abstract, Anime"
                                            />
                                            {error && <p className="text-xs mt-2 text-red-500">{error}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                            <FaSortAmountDown className="text-main-blue text-sm" /> Order
                                        </label>
                                        <div className="md:col-span-2">
                                            <input
                                                type="number"
                                                min="0"
                                                value={orderVal}
                                                onChange={(e) => setOrderVal(e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                            <FaToggleOn className="text-main-blue text-sm" /> Status
                                        </label>
                                        <div className="md:col-span-2">
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={status}
                                                    onChange={() => setStatus(!status)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                                <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-3 pt-2">
                                        <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200">
                                            Reset
                                        </button>
                                        <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2">
                                            <HiOutlinePlus className="text-sm" /> Save Category
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Toolbar: search + filter + sort */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
                            <div className="flex flex-col md:flex-row md:items-center gap-3">
                                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                                    <div className="relative flex-1">
                                        <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                                            placeholder="Search by name..."
                                        />
                                    </div>
                                    <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80">Search</button>
                                </form>

                                <select
                                    value={statusFilter}
                                    onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                                >
                                    <option value="">All Status</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>

                                <select
                                    value={`${sortBy}:${order}`}
                                    onChange={(e) => { const [s, o] = e.target.value.split(':'); setSortBy(s); setOrder(o); setPage(1); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                                >
                                    <option value="w_order:asc">Order ↑</option>
                                    <option value="w_order:desc">Order ↓</option>
                                    <option value="w_name:asc">Name A–Z</option>
                                    <option value="w_name:desc">Name Z–A</option>
                                    <option value="w_view_count:desc">Most Viewed</option>
                                    <option value="w_count:desc">Most Wallpapers</option>
                                    <option value="createdAt:desc">Newest</option>
                                </select>
                            </div>
                        </div>

                        {/* List Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                        <FaImages className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Categories</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            <span className="px-2 py-0.5 bg-main-blue/10 text-main-blue rounded-full text-xs font-medium">
                                                Total: {totalRecords}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                {selected.length > 0 && (
                                    <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2">
                                        <FaTrashAlt className="text-sm" /> Delete Selected ({selected.length})
                                    </button>
                                )}
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {categories.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                            onChange={selectAll}
                                                            checked={selected.length === categories.length && categories.length > 0}
                                                        />
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wallpapers</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {categories.map((c) => (
                                                    <tr key={c._id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-4">
                                                            <input
                                                                type="checkbox"
                                                                className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                                onChange={() => toggleSelect(c._id)}
                                                                checked={selected.includes(c._id)}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{c.w_name}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                                            <div className="flex items-center gap-1.5">
                                                                <span>{c.w_slug}</span>
                                                                <button onClick={() => copySlug(c.w_slug)} className="text-gray-400 hover:text-main-blue" title="Copy slug"><FaCopy size={12} /></button>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{c.w_order}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{c.w_count ?? 0}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{c.w_view_count ?? 0}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={c.w_status}
                                                                    onChange={() => handleToggleStatus(c)}
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                                            <div className="flex items-center gap-2">
                                                                <button onClick={() => setEditCategory(c)} className="flex items-center px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors" title="Edit">
                                                                    <FaEdit />
                                                                </button>
                                                                <button onClick={() => copyId(c._id)} className="flex items-center px-2 py-1 bg-green-100 text-green-600 rounded-md hover:bg-green-200 transition-colors" title="Copy ID">
                                                                    <FaCopy />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        <div className="flex items-center justify-between mt-4 px-1">
                                            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                    disabled={page <= 1}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    Prev
                                                </button>
                                                <button
                                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                                    disabled={page >= totalPages}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Centered Edit Modal */}
            {editCategory && (
                <EditModal
                    category={editCategory}
                    onClose={() => setEditCategory(null)}
                    onSaved={fetchData}
                />
            )}
        </>
    );
};

export default WallpaperCategory;
